import type { ServicePageConfig } from "../../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../../package-pricing";
import { getCityMunicipalityList } from "../../../cities";

const goteborgKommunerText = getCityMunicipalityList("göteborg");

// ─── Flytthjälp Kungälv ────────────────────────────────────────────
export const flytthjalpKungalvPage: ServicePageConfig = {
  slug: "flytthjalp-kungalv",
  serviceCategory: "flytt",
  city: "göteborg",

  meta: {
    title: "Flytthjälp Kungälv – Flyttfirma med Skåpbil | Ungdomskraft",
    description:
      "Boka flytthjälp i Kungälv. Vi bär, kör och lossar i Kungälv, Ytterby, Kareby, Kode och Kärna – skåpbil och personal ingår. Fast pris, RUT-avdrag direkt.",
    h1: "Flytthjälp Kungälv",
  },

  hero: {
    heading: "Flytthjälp i Kungälv",
    intro:
      "Letar du efter en flyttfirma i Kungälv? Ungdomskraft kommer med skåpbil och starka ungdomar och tar hela flytten – från nybygget i Kongahälla eller hyreslägenheten i Komarken till villan i Ytterby, radhuset i Kareby eller huset i Kode och Kärna. Du ser priset direkt när du bokar, RUT-avdraget dras på fakturan och vi har ofta lediga tider samma vecka.",
    heroImage: "/hero-flytthjalp.png",
    heroImageAlt:
      "Ungdomskraft lastar flyttkartonger och möbler i skåpbil vid flytthjälp i Kungälv",
  },

  featureCards: {
    sinceText: "Flytthjälp sedan 2014",
    storyText:
      "Ungdomskraft har hjälpt hushåll i Göteborgsregionen att flytta sedan 2014. Norr om älven kör vi dagligen längs E6 mellan Hisingen och Kungälv, och våra ungdomar är vana vid allt från hisslösa trapphus i centrum till långa bärvägar på villatomter i Ytterby och Kärna.",
    bookYourText: "Boka flytten",
    bookDescText:
      "Skriv in adressen du flyttar från och den du flyttar till, välj vad som ska med och en tid som passar. Priset syns direkt.",
    vehicleTitleText: "Vi kommer med bilen",
    vehicleDescText:
      "Ingen hyrbil, inga lånade släpkärror. Skåpbil, bärare, filtar och spännband ingår – vi lastar i Kungälv och lossar där du ska bo.",
  },

  itemsSection: {
    heading: "Det här flyttar vi oftast i Kungälv",
    items: [
      {
        name: "Soffor & fåtöljer",
        description: "Modulsoffor, bäddsoffor och fåtöljer – inlindade i filt före bärning",
        image: "/sofa.webp",
        alt: "Soffa som bärs ut vid flytthjälp i Kungälv",
      },
      {
        name: "Sängar & madrasser",
        description: "Ramsängar, kontinentalsängar och våningssängar från barnrummet",
        image: "/bed.webp",
        alt: "Säng och madrass som flyttas från villa i Ytterby, Kungälv",
      },
      {
        name: "Matgrupper & skrivbord",
        description: "Matbord med iläggsskivor, stolar och höj- och sänkbara skrivbord",
        image: "/table.webp",
        alt: "Matbord och stolar som lastas vid flytt i Kareby, Kungälv",
      },
      {
        name: "Tvättmaskin & kyl",
        description: "Vitvaror som kopplas loss, säkras i bilen och bärs in på nya stället",
        image: "/washer.webp",
        alt: "Tvättmaskin som flyttas vid flytthjälp i Kongahälla, Kungälv",
      },
      {
        name: "Garderober & hyllor",
        description: "Garderober som tas isär, byråer och bokhyllor fulla av pärmar",
        image: "/wardrobe.webp",
        alt: "Garderob som demonteras inför flytt i Komarken, Kungälv",
      },
      {
        name: "Kartonger & förråd",
        description: "Flyttkartonger, förrådslådor från vinden och saker ur garaget",
        image: "/box.webp",
        alt: "Flyttkartonger som bärs ut från radhus i Kode, Kungälv",
      },
    ],
  },

  seoContent: {
    mainHeading: "Flytthjälp i Kungälv – från lägenhet i centrum till villa på landet",
    mainText:
      "Kungälv ligger ett par mil norr om Göteborg, där Nordre älv delar sig från Göta älv och Bohus fästning vaktar älvmötet. Kommunen har vuxit till drygt 50 000 invånare, och det märks på bostadsmarknaden: i centrala Kungälv har Kongahälla vuxit fram med nya bostadsrätter och hyresrätter i kvarter med egna innergårdar, medan det i Komarken och i övriga centrala Kungälv också finns äldre flerbostadshus där hissen kan vara liten eller saknas. Utanför centrum dominerar villor och radhus – i Ytterby, Kareby, Kode och Kärna bor många barnfamiljer på egna tomter med garage och förråd.\n\nDet vanligaste flyttmönstret vi ser är familjen som lämnar en lägenhet i Göteborg, ofta på Hisingen eller i centrum, för ett hus i Kungälv med trädgård. Då kör vi E6 norrut över Hisingen och är framme i Kungälv på ungefär en kvart när trafiken flyter. Lika vanligt är flyttar inom kommunen: pensionärer som säljer villan i Kärna och flyttar in i en lägenhet med hiss i centrum, eller unga som flyttar hemifrån i Ytterby till en första hyresrätt i Komarken. Vi lastar i den ordning du vill packa upp och bär in allt rum för rum.\n\nPå landsbygden och ute mot kusten ser förutsättningarna annorlunda ut. Grusade uppfarter, smala vägar mot Diseröd och Tjuvkil och hus där bilen inte kommer ända fram gör att vi planerar bärvägen redan vid bokningen. Marstrandsön är bilfri, så en flytt dit eller därifrån kräver särskild planering – beskriv adressen noga när du bokar så återkommer vi om upplägget. För flyttar längs Bohusbanan eller Marstrandsvägen (väg 168) mot Koön lägger vi körschemat så att bilen inte står still i onödan.",
    preparationHeading: "Så förbereder du flytten i Kungälv",
    preparationText:
      "Packa lösa saker i kartonger och skriv rum och innehåll på sidan av lådan – inte bara på locket, eftersom lådorna staplas. Tunga böcker går i små kartonger, lätta täcken i stora. Plocka ner lampor, tavlor och hyllplan dagen innan och lägg skruvar i en påse som tejpas fast på möbeln de hör till.\n\nFlyttar du från villa i Ytterby, Kareby eller Kärna: töm garage och förråd på sådant som inte ska med i god tid, eller boka bortforsling av det samtidigt. Kontrollera att uppfarten är fri och att det finns plats för skåpbilen nära dörren. Bor du i lägenhet i Komarken eller Kongahälla, ta reda på om det finns en lastplats vid entrén och om hissen går att boka. Ha portkoder och nycklar till båda adresserna redo innan vi kommer.",
    additionalSections: [
      {
        heading: "Från Göteborg till Kungälv – så går en typisk familjeflytt",
        content:
          "Många av våra flyttar till Kungälv startar i en lägenhet på Hisingen, i Linnéstaden eller i Majorna. Vi bär ner bohaget, lastar bilen och kör norrut på E6 genom Tingstadstunneln och över Hisingen – snart svänger vi av mot huset i Ytterby eller Kareby. Där bär vi in varje möbel till rätt rum, monterar ihop sängar och garderober och ställer kartongerna i det rum som står på lådan.\n\nKörsträcka inom 20 km ingår i paketet. Blir sträckan längre tillkommer ett km-pris som beror på paketstorleken, och det räknas in i priset du ser innan du bekräftar. Du vet alltså exakt vad flytten kostar innan vi kommer.",
        cta: { text: "Se flytthjälp i Göteborg →", href: "/flytthjalp-goteborg" },
      },
      {
        heading: "Orter vi flyttar i inom Kungälvs kommun",
        content:
          "Vi flyttar i hela kommunen. I centralorten täcker vi Kongahälla, Komarken, Munkegärde och gamla stan längs Västra gatan, där trähusen står tätt och gatorna är smala. Längs E6 och Bohusbanan hjälper vi till i Ytterby och Kode, och i villabygderna Kareby, Kärna och Diseröd. Mot havet flyttar vi i Tjuvkil, på Koön och – efter särskild planering – på Marstrand.\n\nFlytthjälp ger rätt till RUT-avdrag. Du får 50 % avdrag på arbetskostnaden, upp till 75 000 kr per person och år, och vi drar det direkt på fakturan.",
      },
      {
        heading: "Varför boka flytthjälp i Kungälv med Ungdomskraft?",
        listItems: [
          {
            label: "Vi kör E6 varje dag –",
            text: "Sträckan mellan Göteborg och Kungälv är en av våra vanligaste. Vi vet när trafiken står vid Tingstadstunneln och lägger flytten därefter.",
          },
          {
            label: "Villa och lägenhet –",
            text: "Samma team klarar både den trånga trappan i en äldre lägenhet i Komarken och den långa bärvägen från ett garage i Kärna.",
          },
          {
            label: "Pris innan du bokar –",
            text: "Paketen har fasta priser och km-priset över 20 km visas direkt. Ingen offertrunda och inga påslag i efterhand.",
          },
          {
            label: "Försäkring ingår –",
            text: "Alla flyttar är försäkrade genom Trygg Hansa, både dina möbler och fastigheten vi bär i.",
          },
          {
            label: "Allt från en hand –",
            text: "Behöver du bli av med gammalt skräp i samband med flytten kan vi forsla bort det samma dag.",
          },
        ],
      },
    ],
  },

  faq: [
    {
      question: "Vad kostar flytthjälp i Kungälv?",
      answer:
        "Det beror på hur mycket som ska flyttas och hur långt. Några större möbler ryms ofta i paketet Stor (2 495 kr). En etta eller mindre tvåa landar ofta i Extra Stor (4 995 kr, upp till 4 timmars arbete), medan ett helt hus i Ytterby eller Kärna brukar kräva Mega Stor (8 995 kr, upp till 8 timmar). Körsträcka inom 20 km ingår och du ser totalpriset innan du bokar.",
    },
    {
      question: "Är ni en flyttfirma i Kungälv?",
      answer:
        "Ja, vi utför kompletta flyttar i Kungälvs kommun med skåpbil och egen personal. Vi bär, lastar, kör och lossar, och vi demonterar och monterar möbler när det behövs. Du bokar online och får priset direkt i stället för att vänta på en offert.",
    },
    {
      question: "Får jag RUT-avdrag på flytthjälp i Kungälv?",
      answer:
        "Ja. Flytthjälp är RUT-berättigad och du får 50 % avdrag på arbetskostnaden, upp till 75 000 kr per person och år. Vi drar avdraget direkt på fakturan.",
    },
    {
      question: "Vad kostar det att flytta från Göteborg till Kungälv?",
      answer:
        "Körningen mellan Göteborg och Kungälv är ungefär två mil, så en stor del av sträckan ryms i de 20 km som ingår i paketet. Blir det längre, till exempel från södra Göteborg till Kode, läggs ett km-pris till beroende på paketstorlek. Hela beloppet syns i bokningen innan du bekräftar.",
    },
    {
      question: "Kan ni flytta till eller från Marstrand?",
      answer:
        "Marstrandsön är bilfri, så en flytt dit kräver annan planering än en vanlig flytt. Beskriv adressen och vad som ska flyttas när du bokar, så hör vi av oss om upplägget. På Koön och i resten av kommunen kör vi ända fram som vanligt.",
    },
    {
      question: "Hjälper ni med villaflytt i Ytterby, Kareby och Kärna?",
      answer:
        "Ja, villaflyttar är en stor del av våra uppdrag i Kungälv. Vi tömmer hus, garage och förråd, bär ut tunga vitvaror och trädgårdsmöbler och gör flera vändor om bohaget inte ryms i en last.",
    },
    {
      question: "Bär ni i trappor utan hiss i centrala Kungälv?",
      answer:
        "Ja. En del äldre flerbostadshus i centrala Kungälv saknar hiss eller har en hiss som inte rymmer möbler. Vi bär i trapporna utan extra avgift och skyddar väggar och räcken med filtar.",
    },
    {
      question: "Ingår skåpbil i flytthjälpen?",
      answer:
        "Ja, skåpbil och personal ingår alltid. Du behöver inte hyra bil eller släp.",
    },
    {
      question: "Demonterar och monterar ni möbler?",
      answer:
        "Ja, vi tar isär sängar, garderober och bord som inte går hela genom dörren och sätter ihop dem igen i nya hemmet. Lägg gärna monteringsanvisningar i en märkt påse om du har dem kvar.",
    },
    {
      question: "Kan jag boka flytthjälp i Kungälv på helgen?",
      answer:
        "Ja, vi flyttar alla dagar i veckan, även lördag och söndag.",
    },
    {
      question: "Är flytten försäkrad?",
      answer:
        "Ja, alla uppdrag är försäkrade genom Trygg Hansa under hela flytten, från att vi lyfter första kartongen till att sista möbeln står på plats.",
    },
    {
      question: "Kan ni forsla bort det som inte ska med?",
      answer:
        "Ja, vi erbjuder även bortforsling i Kungälv. Möbler och saker du inte vill ha kvar kör vi till återvinningen, så lämnar du ett tomt hus efter dig.",
    },
    {
      question: "Vilka orter i Kungälvs kommun täcker ni?",
      answer:
        "Hela kommunen: centrala Kungälv med Kongahälla, Komarken och Munkegärde, samt Ytterby, Kode, Kareby, Kärna, Diseröd, Tjuvkil och Koön. Marstrand efter särskild planering.",
    },
    {
      question: "Vilka andra kommuner flyttar ni i?",
      answer: `Vi flyttar i hela Göteborgsregionen: ${goteborgKommunerText}. Du kan flytta mellan kommunerna, till exempel från Kungälv till Mölndal.`,
    },
  ],

  priceHint: PACKAGE_PRICE_HINT_SV.transport,

  pricingRows: getPackagePricingRows("transport", "sv"),

  dynamicHeadings: {
    threeSteps: "Flytthjälp i Kungälv i 3 steg",
    step2Text:
      "Teamet kommer till din gamla adress, bär ut och lastar skåpbilen, kör till Kungälv eller vart du nu ska och bär in allt. Du behöver bara visa var sakerna ska stå.",
    step3Title: "Klart i nya hemmet",
    step3Desc:
      "Möblerna står i rätt rum, sängarna är ihopskruvade och kartongerna ligger där de ska packas upp. RUT-avdraget är redan draget på fakturan.",
    ctaHeading: "Boka flytthjälp i Kungälv",
    ctaText:
      "Fyll i båda adresserna och vad som ska med – priset syns direkt, skåpbil och bärare ingår.",
  },

  en: {
    slug: "moving-help-kungalv",
    meta: {
      title: "Moving Help Kungälv – Movers with a Van | Ungdomskraft",
      description:
        "Book moving help in Kungälv. We carry, drive and unload in Kungälv, Ytterby, Kareby, Kode and Kärna – van and crew included. Fixed price, RUT deducted.",
      h1: "Moving Help Kungälv",
    },
    hero: {
      heading: "Moving Help in Kungälv",
      intro:
        "Looking for movers in Kungälv? Ungdomskraft arrives with a van and strong young workers and handles the whole move – from a new build in Kongahälla or a rental flat in Komarken to a house in Ytterby, a terraced house in Kareby or a home in Kode and Kärna. You see the price when you book, the RUT deduction is taken off the invoice and we often have slots the same week.",
      heroImageAlt:
        "Ungdomskraft loading moving boxes and furniture into a van during a move in Kungälv",
    },
    featureCards: {
      sinceText: "Moving help since 2014",
      storyText:
        "Ungdomskraft has helped households in the Gothenburg region move since 2014. North of the river we drive the E6 between Hisingen and Kungälv every day, and our crews are used to everything from lift-less stairwells in the centre to long carries across house plots in Ytterby and Kärna.",
      bookYourText: "Book the move",
      bookDescText:
        "Enter the address you are moving from and the one you are moving to, choose what is coming along and a time that suits you. The price shows instantly.",
      vehicleTitleText: "We bring the van",
      vehicleDescText:
        "No rental van, no borrowed trailer. Van, carriers, blankets and straps are included – we load in Kungälv and unload wherever you are moving.",
    },
    itemsSection: {
      heading: "What we move most often in Kungälv",
      items: [
        {
          name: "Sofas & armchairs",
          description: "Modular sofas, sofa beds and armchairs – wrapped in blankets before carrying",
          image: "/sofa.webp",
          alt: "Sofa being carried out during moving help in Kungälv",
        },
        {
          name: "Beds & mattresses",
          description: "Bed frames, continental beds and bunk beds from the kids' room",
          image: "/bed.webp",
          alt: "Bed and mattress moved from a house in Ytterby, Kungälv",
        },
        {
          name: "Dining sets & desks",
          description: "Extendable dining tables, chairs and sit-stand desks",
          image: "/table.webp",
          alt: "Dining table and chairs loaded during a move in Kareby, Kungälv",
        },
        {
          name: "Washer & fridge",
          description: "Appliances disconnected, secured in the van and carried into the new place",
          image: "/washer.webp",
          alt: "Washing machine moved during moving help in Kongahälla, Kungälv",
        },
        {
          name: "Wardrobes & shelves",
          description: "Wardrobes taken apart, dressers and bookcases full of binders",
          image: "/wardrobe.webp",
          alt: "Wardrobe being disassembled before a move in Komarken, Kungälv",
        },
        {
          name: "Boxes & storage",
          description: "Moving boxes, storage crates from the attic and things from the garage",
          image: "/box.webp",
          alt: "Moving boxes carried out of a terraced house in Kode, Kungälv",
        },
      ],
    },
    seoContent: {
      mainHeading: "Moving help in Kungälv – from a flat in town to a house in the countryside",
      mainText:
        "Kungälv lies about 20 km north of Gothenburg, where the Nordre älv splits off from the Göta älv and Bohus Fortress watches over the meeting of the rivers. The municipality has grown to more than 50,000 residents, and the housing market shows it: in central Kungälv, Kongahälla has grown into a district of new owner-occupied and rental flats around private courtyards, while Komarken and the rest of central Kungälv also have older apartment buildings where the lift may be small or missing. Outside the centre, houses and terraces dominate – in Ytterby, Kareby, Kode and Kärna many families live on their own plots with a garage and shed.\n\nThe most common move we see is a family leaving a flat in Gothenburg, often on Hisingen or in the centre, for a house with a garden in Kungälv. We then take the E6 north across Hisingen and reach Kungälv in about a quarter of an hour when traffic flows. Moves within the municipality are just as common: retirees selling the house in Kärna and moving into a flat with a lift in town, or young people leaving home in Ytterby for a first rental in Komarken. We load in the order you want to unpack and carry everything in room by room.\n\nIn the countryside and towards the coast, conditions are different. Gravel driveways, narrow roads towards Diseröd and Tjuvkil and houses the van cannot reach all the way mean we plan the carrying route when you book. Marstrand island is car-free, so moving there or away from there needs special planning – describe the address carefully when you book and we will get back to you about the arrangement. For moves along the Bohusbanan railway or the Marstrand road (route 168) towards Koön, we schedule the driving so the van is never idle for no reason.",
      preparationHeading: "How to prepare your move in Kungälv",
      preparationText:
        "Pack loose items in boxes and write the room and contents on the side of the box – not just the lid, because boxes get stacked. Heavy books go in small boxes, light duvets in big ones. Take down lamps, pictures and shelves the day before and put screws in a bag taped to the furniture they belong to.\n\nMoving out of a house in Ytterby, Kareby or Kärna: clear the garage and shed of things that are not coming along in good time, or book junk removal for them at the same time. Make sure the driveway is clear and that the van can park near the door. If you live in a flat in Komarken or Kongahälla, find out whether there is a loading spot by the entrance and whether the lift can be booked. Have entry codes and keys for both addresses ready before we arrive.",
      additionalSections: [
        {
          heading: "From Gothenburg to Kungälv – how a typical family move goes",
          content:
            "Many of our moves to Kungälv start in a flat on Hisingen, in Linnéstaden or in Majorna. We carry the household down, load the van and head north on the E6 through the Tingstad tunnel and across Hisingen – soon we turn off towards the house in Ytterby or Kareby. There we carry each piece of furniture to the right room, reassemble beds and wardrobes and put the boxes in the room written on them.\n\nDriving within 20 km is included in the package. If the distance is longer, a per-km price depending on the package size is added, and it is included in the price you see before you confirm. So you know exactly what the move costs before we arrive.",
          cta: { text: "See moving help in Gothenburg →", href: "/en/moving-help-gothenburg" },
        },
        {
          heading: "Areas we move in within Kungälv municipality",
          content:
            "We move across the whole municipality. In the main town we cover Kongahälla, Komarken, Munkegärde and the old town along Västra gatan, where the wooden houses stand close together and the streets are narrow. Along the E6 and the Bohusbanan railway we help in Ytterby and Kode, and in the house-dominated villages of Kareby, Kärna and Diseröd. Towards the sea we move in Tjuvkil, on Koön and – after special planning – on Marstrand.\n\nMoving help qualifies for the Swedish RUT tax deduction. You get 50% off the labour cost, up to SEK 75,000 per person per year, and we deduct it directly on the invoice.",
        },
        {
          heading: "Why book moving help in Kungälv with Ungdomskraft?",
          listItems: [
            {
              label: "We drive the E6 every day –",
              text: "The stretch between Gothenburg and Kungälv is one of our most common. We know when traffic backs up at the Tingstad tunnel and plan the move around it.",
            },
            {
              label: "Houses and flats –",
              text: "The same crew handles both the tight staircase in an older flat in Komarken and the long carry from a garage in Kärna.",
            },
            {
              label: "Price before you book –",
              text: "The packages have fixed prices and the per-km price above 20 km is shown instantly. No quote rounds and no surcharges afterwards.",
            },
            {
              label: "Insurance included –",
              text: "Every move is insured through Trygg Hansa, covering both your furniture and the property we carry in.",
            },
            {
              label: "One provider –",
              text: "If you need to get rid of old junk in connection with the move, we can remove it the same day.",
            },
          ],
        },
      ],
    },
    faq: [
      {
        question: "How much does moving help in Kungälv cost?",
        answer:
          "It depends on how much is moving and how far. A few larger pieces often fit in the Large package (SEK 2,495). A studio or small one-bed usually lands in Extra Large (SEK 4,995, up to 4 hours of work), while a whole house in Ytterby or Kärna usually needs Mega Large (SEK 8,995, up to 8 hours). Driving within 20 km is included and you see the total before you book.",
      },
      {
        question: "Are you a removal company in Kungälv?",
        answer:
          "Yes, we carry out complete moves in Kungälv municipality with a van and our own staff. We carry, load, drive and unload, and we take furniture apart and put it back together when needed. You book online and get the price instantly instead of waiting for a quote.",
      },
      {
        question: "Do I get the RUT deduction on moving help in Kungälv?",
        answer:
          "Yes. Moving help qualifies for RUT and you get 50% off the labour cost, up to SEK 75,000 per person per year. We deduct it directly on the invoice.",
      },
      {
        question: "What does it cost to move from Gothenburg to Kungälv?",
        answer:
          "The drive between Gothenburg and Kungälv is roughly 20 km, so much of the distance fits within the 20 km included in the package. If it is longer, for example from southern Gothenburg to Kode, a per-km price is added depending on package size. The full amount is shown in the booking before you confirm.",
      },
      {
        question: "Can you move to or from Marstrand?",
        answer:
          "Marstrand island is car-free, so a move there needs different planning from an ordinary move. Describe the address and what is moving when you book and we will get in touch about the arrangement. On Koön and in the rest of the municipality we drive right up as usual.",
      },
      {
        question: "Do you help with house moves in Ytterby, Kareby and Kärna?",
        answer:
          "Yes, house moves are a large share of our jobs in Kungälv. We empty houses, garages and sheds, carry out heavy appliances and garden furniture and make several trips if the household doesn't fit in one load.",
      },
      {
        question: "Do you carry up stairs without a lift in central Kungälv?",
        answer:
          "Yes. Some older apartment buildings in central Kungälv have no lift or a lift too small for furniture. We carry on the stairs at no extra charge and protect walls and railings with blankets.",
      },
      {
        question: "Is a van included?",
        answer:
          "Yes, a van and crew are always included. You don't need to rent a vehicle or trailer.",
      },
      {
        question: "Do you disassemble and reassemble furniture?",
        answer:
          "Yes, we take apart beds, wardrobes and tables that won't fit through the door whole and put them back together in the new home. Keep assembly instructions in a labelled bag if you still have them.",
      },
      {
        question: "Can I book moving help in Kungälv at the weekend?",
        answer: "Yes, we move every day of the week, including Saturday and Sunday.",
      },
      {
        question: "Is the move insured?",
        answer:
          "Yes, every job is insured through Trygg Hansa throughout the move, from the first box we lift until the last piece of furniture is in place.",
      },
      {
        question: "Can you take away what isn't coming along?",
        answer:
          "Yes, we also offer junk removal in Kungälv. Furniture and items you don't want to keep are driven to recycling, so you leave an empty house behind.",
      },
      {
        question: "Which areas in Kungälv municipality do you cover?",
        answer:
          "The whole municipality: central Kungälv with Kongahälla, Komarken and Munkegärde, plus Ytterby, Kode, Kareby, Kärna, Diseröd, Tjuvkil and Koön. Marstrand after special planning.",
      },
      {
        question: "Which other municipalities do you move in?",
        answer: `We move across the whole Gothenburg region: ${goteborgKommunerText}. You can move between municipalities, for example from Kungälv to Mölndal.`,
      },
    ],
    priceHint: PACKAGE_PRICE_HINT_EN.transport,
    pricingRows: getPackagePricingRows("transport", "en"),
    dynamicHeadings: {
      threeSteps: "Moving help in Kungälv in 3 steps",
      step2Text:
        "The crew arrives at your old address, carries out and loads the van, drives to Kungälv or wherever you are going and carries everything in. All you do is show where things go.",
      step3Title: "Done in the new home",
      step3Desc:
        "Furniture is in the right rooms, beds are screwed together and boxes are where they will be unpacked. The RUT deduction is already taken off the invoice.",
      ctaHeading: "Book moving help in Kungälv",
      ctaText:
        "Fill in both addresses and what is coming along – the price shows instantly, van and carriers included.",
    },
  },
};
